import { AfterViewChecked, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { EmailCreateTabManagerService } from '@services/email-create-tab-manager.service';
import { EmailHistoryService } from '@services/email-history.service';
import { EmailSendingService } from '@services/email-sending.service';
import Hashids from 'hashids';
import { ToastContainerDirective } from 'ngx-toastr';

@Component({
  selector: 'wsb-email-create',
  templateUrl: './email-create.component.html',
  styleUrls: ['./email-create.component.scss'],
  providers: [EmailCreateTabManagerService],
  standalone: false
})
export class EmailCreateComponent implements OnInit, AfterViewChecked {
  @ViewChild(ToastContainerDirective) toastContainer: ToastContainerDirective;
  @ViewChild('modalSuccess') modalSuccess;
  @ViewChild('modalSubscriptionError') modalSubscriptionError;
  @ViewChild('page', { static: true }) $page;
  @ViewChild('actionsBar', { static: true }) $actionsBar;
  modalSuccessModal: NgbModalRef;
  modalSubscriptionErrorModal: NgbModalRef;

  currentStep = 0;
  nextButtonEnable = true;
  summary = false;
  emailCampaignIssue;
  templateId;
  template;
  hashids;
  showSummaryButton = true;
  constructor(
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private router: Router,
    public emailCreateTabManagerService: EmailCreateTabManagerService,
    private emailHistoryService: EmailHistoryService,
    private emailSendingsService: EmailSendingService,
    private elRef: ElementRef
  ) {}

  ngOnInit(): void {
    window.addEventListener('scroll', this.scroll, true);

    this.checkRoute();

    this.emailCreateTabManagerService.stepChange$.subscribe(step => {
      if (step) {
        this.currentStep = step;
      }
    });

    this.emailCreateTabManagerService.lgBack$.subscribe(step => {
      if (step && step !== 4) {
        this.back();
      }
    });

    this.emailCreateTabManagerService.lgDone$.subscribe(value => {
      this.showSummaryButton = false;

      switch (value) {
        case 'SubError':
          this.openModalSubscriptionError();
          break;
        case 'recipients':
          this.summary = true;
          this.back();
          break;

        case 'back':
          this.back();
          break;

        case 'summary':
          this.summary = true;
          this.showSummaryButton = true;
          this.tabIsReady();
          break;

        case 'editSetUp':
          this.summary = true;
          this.editSetup();
          break;

        case 'changeTemplate':
          this.summary = true;
          // this.tabIsReady();
          break;

        case 'editor':
          this.alreadyHasTemplate();
          break;

        case 'copy':
          this.saveTemplate();
          break;

        case 'send':
          this.sendEmailCampaign();
          break;

        default:
          this.tabIsReady();
          break;
      }
    });
  }

  ngAfterViewChecked(): void {
    this.displayScrollIndicator();
  }

  scroll = (): void => {
    this.displayScrollIndicator();
  };

  tabIsReady() {
    this.emailCreateTabManagerService.tabIsReady.subscribe(
      data => {
        if (data.idCampaignIssue) {
          this.emailCampaignIssue = data;
        }

        this.next();
      },
      err => {
        this.emailCreateTabManagerService.errorHandler(err.error.error_message, this.toastContainer);
      }
    );
  }

  sendEmailCampaign() {
    this.emailCreateTabManagerService.send.subscribe(
      data => {
        this.openSendSuccessModal();
      },
      err => {
        this.emailCreateTabManagerService.errorHandler(err.error.error_message, this.toastContainer);
      }
    );
  }

  saveTemplate() {
    this.emailCreateTabManagerService.tabIsReady.subscribe(
      data => {
        this.emailCampaignIssue = data;
        if (this.template) {
          const body = {
            messageHtml: this.template,
            messageText: this.emailCampaignIssue.messageText
          };
          this.emailSendingsService.saveBodyHtml(data.idCampaignIssue, body).subscribe(issue => {
            this.emailCampaignIssue = issue;
            this.goToEditor();
          });
        } else {
          this.goToEditor();
        }
      },
      err => {
        this.emailCreateTabManagerService.errorHandler(err.error.error_message, this.toastContainer);
      }
    );
  }

  editSetup() {
    this.emailCreateTabManagerService.tabIsReady.subscribe(
      data => {
        if (data.idCampaignIssue) {
          this.emailCampaignIssue = data;
        }
      },
      err => {
        this.emailCreateTabManagerService.errorHandler(err.error.error_message, this.toastContainer);
      }
    );
  }

  alreadyHasTemplate() {
    this.emailCreateTabManagerService.tabIsReady.subscribe(
      data => {
        this.emailCampaignIssue = data;

        this.goToEditor();
      },
      err => {
        this.emailCreateTabManagerService.errorHandler(err.error.error_message, this.toastContainer);
      }
    );
  }

  checkIfHasTemplate() {
    if (this.templateId) {
      return true;
    }
    if (this.template) {
      return true;
    }
    if (this.emailCampaignIssue) {
      if (this.emailCampaignIssue.messageHtml) {
        return true;
      }
    }
    return false;
  }

  goToEditor() {
    this.createHashIds();
    window.location.href = '/mail/mail-editor?issue=' + this.hashids.encode(this.emailCampaignIssue.idCampaignIssue) + '&new_version=true';
  }

  createHashIds() {
    this.translateService.get('pages_salt_hashids').subscribe(salt => {
      this.hashids = new Hashids(salt, 26, 'ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890');
    });
  }

  backButton() {
    this.emailCreateTabManagerService.lgBack();
  }

  back() {
    this.emailCreateTabManagerService.back();
  }

  next() {
    if (this.summary) {
      this.emailCreateTabManagerService.setCurrentStep(5);
    } else {
      this.emailCreateTabManagerService.next();
    }
    this.summary = false;
  }

  nextButton() {
    this.emailCreateTabManagerService.lgNext();
  }

  checkRoute() {
    this.route.queryParams.subscribe(params => {
      if (params.template) {
        this.templateId = params.template;
      }
      if (params.issue) {
        if (params.step) {
          return this.decodeHashIds(params.issue, params.step);
        }
        return this.decodeHashIds(params.issue);
      }
      this.currentStep = 1;
    });
  }

  decodeHashIds(issue, step?) {
    this.translateService.get('pages_salt_hashids').subscribe(salt => {
      const hashids = new Hashids(salt, 26, 'ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890');
      const issueId = hashids.decode(issue)[0];
      this.getEmailIssueCampaign(issueId, step);
    });
  }

  getEmailIssueCampaign(issueId, step?) {
    this.emailHistoryService.getEmailCampaignIssue(issueId).subscribe(data => {
      this.emailCampaignIssue = data;
      if (step === '2') {
        this.emailCreateTabManagerService.setCurrentStep(2);
      } else if (step) {
        this.emailCreateTabManagerService.setCurrentStep(step);
      } else {
        this.emailCreateTabManagerService.setCurrentStep(4);
      }
    });
  }

  enableButton(value) {
    this.nextButtonEnable = value;
  }

  openModalSubscriptionError() {
    this.modalSubscriptionErrorModal = this.modalService.open(this.modalSubscriptionError);

    this.modalSubscriptionErrorModal.result.then(
      result => {},
      reason => {}
    );
  }

  goToShop() {
    this.modalSubscriptionErrorModal.close();
    this.router.navigate(['/shop/index']);
  }

  openSendSuccessModal() {
    this.modalSuccessModal = this.modalService.open(this.modalSuccess);

    this.modalSuccessModal.result.then(
      result => {},
      reason => {}
    );
  }

  goToEmailCampaignHistory() {
    this.modalSuccessModal.close();
    this.router.navigate(['/mail/list']);
  }

  hasTemplate(template) {
    this.template = template;
  }

  selectedTemplateId(id) {
    this.templateId = id;
  }

  private displayScrollIndicator() {
    if (!(this.$page && this.$actionsBar)) {
      return;
    }
    const $pageAccount: HTMLElement = this.$page.nativeElement;
    const $actionsBar: HTMLElement = this.$actionsBar.nativeElement;
    const $impersonateHeader: HTMLElement = document.getElementById('impersonate-header');

    if (!this.elRef.nativeElement || !this.elRef.nativeElement.closest('body')) {
      return;
    }

    const remainingHeight = window.innerHeight - $pageAccount.getBoundingClientRect().top;

    if (
      $pageAccount.clientHeight > remainingHeight + 5 &&
      $pageAccount.getBoundingClientRect().bottom - window.innerHeight - $impersonateHeader.getBoundingClientRect().bottom > 50
    ) {
      $actionsBar.classList.add('has--scroll-indicator');
    } else {
      $actionsBar.classList.remove('has--scroll-indicator');
    }
  }
}
